import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CreditCard, ExternalLink, Sparkles, CheckCircle2, Clock, FileText, ArrowUpRight, XCircle,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  free:      "bg-zinc-200 text-zinc-800",
  trialing:  "bg-amber-100 text-amber-800",
  active:    "bg-emerald-100 text-emerald-800",
  past_due:  "bg-orange-100 text-orange-800",
  canceled:  "bg-zinc-200 text-zinc-500 line-through",
  unpaid:    "bg-red-100 text-red-800",
  unknown:   "bg-zinc-200 text-zinc-800",
};

const money = (amount, currency = "usd") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() })
    .format((amount || 0) / 100);

export default function Billing() {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/billing/status").then((r) => setB(r.data));
  useEffect(() => { load(); }, []);

  const startCheckout = async (lookup_key, trial_days) => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key, quantity: 1, origin_url: window.location.origin, trial_days,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal", {
        return_url: `${window.location.origin}/app/billing`,
      });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Portal unavailable");
      setBusy(false);
    }
  };

  if (!b) return <div className="p-8 font-mono-plex text-sm">Loading billing…</div>;

  const onPaid = b.subscription_status === "active" || b.subscription_status === "trialing";
  const isFree = !b.plan;

  return (
    <div className="p-8 space-y-6" data-testid="billing-page">
      <div>
        <div className="overline text-primary">/ BILLING &amp; SUBSCRIPTION</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Billing</h1>
      </div>

      {/* Current plan */}
      <div className="border border-border bg-card p-8" data-testid="current-plan-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="overline text-muted-foreground">Current Plan</div>
            <div className="mt-3 flex items-baseline gap-3">
              <span className="font-display text-4xl font-black tracking-tight" data-testid="plan-name">
                {b.plan_name}
              </span>
              <Badge className={`${STATUS_COLOR[b.subscription_status] || STATUS_COLOR.unknown} rounded-sm font-mono-plex text-[10px] uppercase tracking-widest`} data-testid="plan-status">
                {b.subscription_status}
              </Badge>
            </div>
            <div className="mt-2 text-sm text-muted-foreground">
              {isFree
                ? "You're on the free Starter plan."
                : `${money(b.amount, b.currency)} · ${b.cadence}`}
            </div>
            {b.trial_days_remaining !== null && b.trial_days_remaining !== undefined && b.subscription_status === "trialing" && (
              <div className="mt-3 inline-flex items-center gap-2 text-sm px-3 py-1.5 border border-amber-200 bg-amber-50 text-amber-800 rounded-sm" data-testid="trial-remaining">
                <Clock className="w-4 h-4" />
                Trial ends in <span className="font-bold">{b.trial_days_remaining} days</span>
              </div>
            )}
            {b.current_period_end && b.subscription_status === "active" && (
              <div className="mt-3 text-xs font-mono-plex text-muted-foreground uppercase tracking-widest">
                Renews {new Date(b.current_period_end).toLocaleDateString()}
                {b.cancel_at_period_end && " · Cancels at period end"}
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {isFree && (
              <Button
                onClick={() => startCheckout("business_monthly", 14)}
                disabled={busy}
                data-testid="upgrade-to-business"
                className="rounded-sm bg-accent hover:bg-accent/90 text-white"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Start 14-day trial
              </Button>
            )}
            {onPaid && b.has_customer && (
              <Button
                onClick={openPortal}
                disabled={busy}
                data-testid="manage-portal"
                variant="outline"
                className="rounded-sm"
              >
                <ExternalLink className="w-4 h-4 mr-2" /> Manage in Stripe
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Upgrade tiles for free/trial users */}
      {!onPaid && (
        <div className="grid md:grid-cols-2 gap-4">
          <PlanTile
            testId="tile-business-monthly"
            name="Business" price={money(4900)} cadence="/user/mo"
            highlight
            perks={["Unlimited SOPs & modules", "Workflow approvals", "AI everywhere", "14-day free trial"]}
            cta="Start 14-day trial"
            onClick={() => startCheckout("business_monthly", 14)}
            busy={busy}
          />
          <PlanTile
            testId="tile-business-yearly"
            name="Business · Yearly" price={money(47040)} cadence="/user/yr"
            perks={["Everything in Business", "Save 20% vs monthly", "Priority support"]}
            cta="Save 20% yearly"
            onClick={() => startCheckout("business_yearly")}
            busy={busy}
          />
        </div>
      )}

      {/* Invoices */}
      <div className="border border-border bg-card" data-testid="invoices-panel">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="overline text-muted-foreground">Invoices</div>
          <FileText className="w-4 h-4 text-muted-foreground" />
        </div>
        {b.invoices.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            No invoices yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-border">
              <tr className="text-left">
                <th className="px-4 py-3 overline text-muted-foreground">Number</th>
                <th className="px-4 py-3 overline text-muted-foreground">Amount</th>
                <th className="px-4 py-3 overline text-muted-foreground">Status</th>
                <th className="px-4 py-3 overline text-muted-foreground">Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {b.invoices.map((i) => (
                <tr key={i.id} className="border-b border-border" data-testid={`invoice-${i.id}`}>
                  <td className="px-4 py-3 font-mono-plex text-xs">{i.number || i.id.slice(0, 12)}</td>
                  <td className="px-4 py-3 font-medium">{money(i.amount_paid || i.amount_due, i.currency)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-mono-plex uppercase tracking-widest ${i.status === "paid" ? "text-emerald-600" : "text-muted-foreground"}`}>
                      {i.status === "paid" ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                      {i.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {new Date(i.created).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {i.hosted_invoice_url && (
                      <a href={i.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-primary hover:underline text-xs inline-flex items-center gap-1">
                        View <ArrowUpRight className="w-3 h-3" />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-xs font-mono-plex uppercase tracking-widest text-muted-foreground">
        ◉ Payments processed securely by Stripe. Cancel anytime.
      </p>
    </div>
  );
}

function PlanTile({ testId, name, price, cadence, perks, cta, onClick, highlight, busy }) {
  return (
    <div className={`border p-6 ${highlight ? "border-accent bg-zinc-950 text-white" : "border-border bg-card"}`} data-testid={testId}>
      <div className={`overline ${highlight ? "text-accent" : "text-primary"}`}>{name}</div>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="font-display text-4xl font-black tracking-tight">{price}</span>
        <span className={`text-sm ${highlight ? "text-zinc-400" : "text-muted-foreground"}`}>{cadence}</span>
      </div>
      <ul className="mt-5 space-y-2 text-sm">
        {perks.map((p) => (
          <li key={p} className="flex items-start gap-2">
            <CheckCircle2 className={`w-4 h-4 shrink-0 mt-0.5 ${highlight ? "text-accent" : "text-primary"}`} />
            <span>{p}</span>
          </li>
        ))}
      </ul>
      <Button
        onClick={onClick}
        disabled={busy}
        className={`w-full mt-6 rounded-sm ${highlight ? "bg-accent hover:bg-accent/90 text-white" : "bg-primary hover:bg-primary/90"}`}
        data-testid={`${testId}-cta`}
      >
        {cta}
      </Button>
    </div>
  );
}
